// @ts-nocheck
import React, { useState } from "react";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import Button from "@store/MobiusOverrides/Button/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@store/MobiusOverrides/Checkbox/Checkbox";
import {
    EditableInvoicePaymentDto,
    EditableInvoicePaymentOption,
} from "@store/Framework/Store/Pages/AccountBalance/AccountBalanceReducer";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import useHandleApiError from "@store/Framework/Common/Hooks/useHandleApiError";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import TextField from "@store/MobiusOverrides/TextField/TextField";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import Lock from "@insite/mobius/Icons/Lock";
import siteMessage from "@insite/client-framework/SiteMessage";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import { DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { is } from "immer/dist/internal";
import { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";
import { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import useAccountBalanceState, {
    useIsAccountBalanceLoading,
} from "@store/Framework/Common/Hooks/AccountBalance/useAccountBalanceState";

const StyledHr = getStyledWrapper("hr");
const defaultAccountBalance: Omit<EditableInvoicePaymentDto, "paymentOptions"> = {
    termsDescription: "",
    balance: 0,
    overdue: 0,
    current: 0,
    lastPaymentAmount: 0,
    lastInvoiceDate: new Date(),
    credits: [] as EditableInvoicePaymentOption[],
    lastPayDate: new Date(),
    payments: [] as EditableInvoicePaymentOption[],
    canPayOnline: false,
    invoicePaymentEnabled: true,
    accountDepositEnabled: true,
    accountDepositEnabledUntil: new Date(),
    accountDepositMaximum: 0,
};

interface AccountTableStyles {
    button?: ButtonPresentationProps;
    dataTable?: DataTablePresentationProps;
    cell?: DataTableCellProps;
    labelTypographyProps?: DataTableCellProps["typographyProps"];
    cardWrapper?: InjectableCss;
    widgetWrapper?: InjectableCss;
    paymentSummaryWrapper?: InjectableCss;
    paymentActionsWrapper?: InjectableCss;
    totalPaymentText?: TypographyPresentationProps;
    paymentRow?: InjectableCss;
    horizontalRule?: InjectableCss;
    headerCell?: DataTableCellProps;
    tableWrapper?: InjectableCss;
    textField?: TextFieldPresentationProps;
    infosBox?: InfosBoxProps;
}

const accountTableStyles: AccountTableStyles = {
    textField: {
        cssOverrides: {
            inputSelect: css`
                width: 100px;
            `,
        },
    },
    tableWrapper: {
        css: css`
            overflow: auto;
            ${breakpoint("sm", "down")} {
                overflow: scroll;
                max-height: 100vh;
                th {
                    position: sticky;
                    top: 0;
                }
            }
        `,
    },
    dataTable: {
        css: css`
            overflow: auto;
        `,
    },
    paymentRow: {
        css: css`
            display: flex;
            gap: ${pxToRem(30)};
            justify-content: space-between;
        `,
    },
    totalPaymentText: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
        `,
    },
    labelTypographyProps: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
        `,
    },
    cell: {
        css: css`
            border: 1px solid ${getColor("common.border")};
        `,
    },
    headerCell: {
        css: css`
            border: 1px solid ${getColor("common.border")};
        `,
        typographyProps: {
            css: css`
                font-weight: ${({ theme }) => theme.design.typography.weight.semibold};
            `,
        },
    },
    button: {
        icon: {
            src: Lock,
            position: "left",
        },
    },
    horizontalRule: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            width: 100%;
            margin: 0;
        `,
    },
    cardWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            box-shadow: ${({ theme }) => theme.shadows["small"]};
            padding: ${pxToRem(20)};
            border-radius: ${pxToRem(10)};
            gap: ${pxToRem(10)};
            ${breakpoint("sm", "down")} {
                overflow-x: hidden;
                max-width: 100vw;
            }
        `,
    },
    widgetWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
        `,
    },
    paymentSummaryWrapper: {
        css: css`
            display: flex;
            width: fit-content;
            padding-block: ${pxToRem(20)};
            justify-content: flex-end;
            width: 100%;
        `,
    },
    paymentActionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
            background: ${getColor("common.shadesLight")};
            justify-content: space-evenly;
            min-height: ${pxToRem(150)};
            border-radius: 10px;
            margin-left: auto;
            border: 1px solid ${getColor("common.border")};
            padding: ${pxToRem(12)};
            align-items: center;
            line-height: 1;
        `,
    },
    infosBox: {
        extendedStyles: {
            content: {
                css: css`
                    max-width: ${pxToRem(200)};
                `,
            },
            wrapper: {
                css: css`
                    align-self: flex-start;
                    max-width: 100%;
                    flex-grow: 0;
                `,
            },
        },
    },
};

const InvoicePaymentTable: React.FC = () => {
    const [focusedElementId, setFocusedElementId] = useState<string | null>(null);
    const styles = accountTableStyles;
    const isLoading = useIsAccountBalanceLoading();
    const accountBalanceMessage = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.accountBalance.accountMessage,
    );
    const dispatch = useBunzlDispatch();
    const { accountBalance = defaultAccountBalance } = useAccountBalanceState();
    const currencyFormatter = useLocalizeCurrency();
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const handlePayNowClick = () => {
        if (!hasSubmitted) {
            setHasSubmitted(true);
        }
        if (validateForm()) {
            dispatch({ type: "Pages/AccountBalance/ToggleSubmitting" });
        }
    };

    const total = accountBalance?.payments.length
        ? accountBalance?.payments?.reduce((acc, option) => acc + (option.isEditing ? option.paymentAmount ?? 0 : 0), 0)
        : 0;

    const validateForm = () => {
        const editingPayments = accountBalance?.payments.filter(option => option.isEditing);
        const allEditingPaymentsHaveValue = editingPayments?.every(option => option.paymentAmount ?? 0 > 0);
        return allEditingPaymentsHaveValue;
    };

    const canSubmit = total > 0 && (validateForm() || !hasSubmitted);

    const handleInvoiceEditableToggle = (invoiceNumber: string) => {
        dispatch({ type: "Pages/AccountBalance/InvoiceEditableChecked", invoiceNumber });
    };

    const handlePaymentAmountChanged = (event: React.ChangeEvent<HTMLInputElement>, invoiceNumber: string) => {
        const paymentAmountStr = event.target.value ?? "";
        const regex = /^-?\d+(?:\.\d{0,2})?/;
        const paymentAmount = paymentAmountStr === "" ? undefined : Number(paymentAmountStr.match(regex)?.[0] ?? "");

        dispatch({ type: "Pages/AccountBalance/UpdateInvoicePayment", invoiceNumber, paymentAmount });
    };

    if (!accountBalance.invoicePaymentEnabled) {
        return null;
    }

    if (accountBalance.payments.length === 0 && !isLoading) {
        return <Typography>{siteMessage("AccountBalance_NoPayments")}</Typography>;
    }

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.widgetWrapper}>
                <StyledWrapper {...styles.cardWrapper}>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                {accountBalance.canPayOnline && <DataTableHeader {...styles.cell}></DataTableHeader>}
                                <DataTableHeader {...styles.headerCell}>{translate("Invoice #")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Order #")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Date")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Type")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("PO Number")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Total Amount")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Balance")}</DataTableHeader>
                                <DataTableHeader {...styles.headerCell}>{translate("Due Date")}</DataTableHeader>
                                {accountBalance.canPayOnline && (
                                    <DataTableHeader {...styles.headerCell}>{translate("Payment")}</DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody>
                                {accountBalance.payments.map((option: EditableInvoicePaymentOption) => {
                                    if (option.type === "Invoice" && !accountBalance.invoicePaymentEnabled) return null;
                                    if (option.type === "Deposit" && !accountBalance.accountDepositEnabled) return null;
                                    const isFocused = focusedElementId === option.invoiceNumber;
                                    const value = isFocused ? option.paymentAmount : option.paymentAmount?.toFixed(2);
                                    const isError = hasSubmitted && option.isEditing && !option.paymentAmount;
                                    const error = isError ? "Payment amount is required" : undefined;
                                    return (
                                        <DataTableRow key={option.invoiceNumber}>
                                            {accountBalance.canPayOnline && (
                                                <DataTableCell {...styles.cell}>
                                                    <Checkbox
                                                        checked={option.isEditing}
                                                        onChange={() =>
                                                            handleInvoiceEditableToggle(option.invoiceNumber)
                                                        }
                                                    />
                                                </DataTableCell>
                                            )}
                                            <DataTableCell {...styles.cell}>{option.invoiceNumber}</DataTableCell>
                                            <DataTableCell {...styles.cell}>{option.orderNumber}</DataTableCell>
                                            <DataTableCell {...styles.cell}>
                                                {new Date(option.date).toLocaleDateString()}
                                            </DataTableCell>
                                            <DataTableCell {...styles.cell}>{option.type}</DataTableCell>
                                            <DataTableCell {...styles.cell}>{option.customerPo}</DataTableCell>
                                            <DataTableCell {...styles.cell}>
                                                {option.originalAmount != null
                                                    ? currencyFormatter(option.originalAmount)
                                                    : ""}
                                            </DataTableCell>
                                            <DataTableCell {...styles.cell}>
                                                {currencyFormatter(option.balanceDue)}
                                            </DataTableCell>
                                            <DataTableCell {...styles.cell}>
                                                {new Date(option.dateDue).toLocaleDateString()}
                                            </DataTableCell>
                                            {accountBalance.canPayOnline && (
                                                <DataTableCell {...styles.cell}>
                                                    <TextField
                                                        onFocus={() => setFocusedElementId(option.invoiceNumber)}
                                                        onBlur={() => setFocusedElementId(null)}
                                                        value={option.isEditing ? value : ""}
                                                        onChange={event =>
                                                            handlePaymentAmountChanged(event, option.invoiceNumber)
                                                        }
                                                        disabled={!option.isEditing}
                                                        error={error}
                                                        type="number"
                                                        min={0}
                                                        max={
                                                            option.type == "Invoice"
                                                                ? option.balanceDue
                                                                : option.originalAmount
                                                        }
                                                        {...styles.textField}
                                                    />
                                                </DataTableCell>
                                            )}
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    {accountBalance.canPayOnline && (
                        <StyledWrapper {...styles.paymentSummaryWrapper}>
                            <StyledWrapper {...styles.paymentActionsWrapper}>
                                <StyledWrapper {...styles.paymentRow}>
                                    <Typography {...styles.totalPaymentText}>
                                        {`${translate("Total Payment Amount")}:`}
                                    </Typography>
                                    <Typography {...styles.totalPaymentText}>
                                        {currencyFormatter(total ?? 0)}
                                    </Typography>
                                </StyledWrapper>
                                <StyledHr {...styles.horizontalRule} />

                                <Button {...styles.button} disabled={!canSubmit} onClick={handlePayNowClick}>
                                    {siteMessage("AccountBalance_PayButton")}
                                </Button>

                                {accountBalanceMessage && (
                                    <>
                                        <StyledHr {...styles.horizontalRule} />
                                        <InfosBox {...styles.infosBox} type={accountBalanceMessage.type}>
                                            {accountBalanceMessage.message}
                                        </InfosBox>
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: CustomWidgetModule = {
    component: InvoicePaymentTable,
    definition: {
        group: "Payments",
        displayName: "Account Balance Table",
    },
};

export default widgetModule;
